// Plans — list the agency's plans grouped by groupKey (tier/price/saasProducts),
// an "offered" toggle per plan (PUT /plans/config), a "Sync from HighLevel" button
// (POST /plans/sync), and a content-gating editor (area → saasProduct) via
// /content/gating.
import { useEffect, useMemo, useState } from 'react';
import type { GatingRow, Plan } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, Chip, EmptyNote, ErrorNote, SectionHeader, Select, Spinner, Toggle } from './ui';

const UNGATED = '__none__';

export default function Plans() {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  function load() {
    setError(null);
    agencyApi
      .plansConfig()
      .then((p) => setPlans(p))
      .catch((e: Error) => setError(e.message));
  }
  useEffect(load, []);

  const groups = useMemo(() => {
    if (!plans) return [];
    const byKey = new Map<string, Plan[]>();
    for (const p of plans) {
      const key = p.groupKey ?? 'ungrouped';
      const list = byKey.get(key) ?? [];
      list.push(p);
      byKey.set(key, list);
    }
    return [...byKey.entries()].map(([key, list]) => ({
      key,
      plans: [...list].sort((a, b) => a.tierOrder - b.tierOrder),
    }));
  }, [plans]);

  // All saasProduct slugs seen across plans — the options for the gating editor.
  const saasProducts = useMemo(() => {
    const set = new Set<string>();
    for (const p of plans ?? []) for (const s of p.saasProducts) set.add(s);
    return [...set].sort();
  }, [plans]);

  async function toggleOffered(plan: Plan, offered: boolean) {
    if (!plans) return;
    setPlans(plans.map((p) => (p.id === plan.id ? { ...p, offered } : p)));
    setSavingId(plan.id);
    try {
      const updated = await agencyApi.savePlansConfig([{ id: plan.id, offered }]);
      if (updated.length) setPlans(updated);
    } catch (e) {
      setError((e as Error).message);
      load();
    } finally {
      setSavingId(null);
    }
  }

  async function sync() {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res = await agencyApi.syncPlans();
      setSyncNote(typeof res.count === 'number' ? `Synced ${res.count} plan(s) from HighLevel.` : 'Sync complete.');
      load();
    } catch (e) {
      setSyncNote(`Sync failed: ${(e as Error).message}`);
    } finally {
      setSyncing(false);
    }
  }

  if (error) return <ErrorNote error={error} />;
  if (!plans) return <Spinner />;

  return (
    <div className="space-y-8">
      <div>
        <SectionHeader
          title="SaaS Plans"
          subtitle="Toggle which plans you offer. Offered plans drive the customer-facing upgrade nudges within each group."
          action={
            <div className="flex items-center gap-3">
              {syncNote && <span className="text-xs text-slate-500">{syncNote}</span>}
              <Button variant="secondary" onClick={sync} disabled={syncing}>
                {syncing ? 'Syncing…' : '↻ Sync from HighLevel'}
              </Button>
            </div>
          }
        />

        {groups.length === 0 ? (
          <EmptyNote>No plans yet. Click “Sync from HighLevel” to import your SaaS plans.</EmptyNote>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.key}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {g.key === 'ungrouped' ? 'Ungrouped' : g.key}
                </h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {g.plans.map((p) => (
                    <Card key={p.id} className={`p-4 ${p.offered ? '' : 'opacity-70'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-slate-900">{p.name}</div>
                          <div className="text-sm text-slate-500">
                            {p.price ?? 'Custom'} · tier {p.tierOrder}
                          </div>
                        </div>
                        <Toggle
                          checked={p.offered}
                          onChange={(v) => toggleOffered(p, v)}
                          disabled={savingId === p.id}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {p.saasProducts.length ? (
                          p.saasProducts.map((s) => <Chip key={s} label={s} muted />)
                        ) : (
                          <span className="text-xs text-slate-400">No SaaS products listed</span>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <GatingEditor saasProducts={saasProducts} ungated={UNGATED} />
    </div>
  );
}

function GatingEditor({ saasProducts, ungated }: { saasProducts: string[]; ungated: string }) {
  const [rows, setRows] = useState<GatingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newArea, setNewArea] = useState('');

  useEffect(() => {
    agencyApi
      .gating()
      .then((r) => setRows(r))
      .catch((e: Error) => setError(e.message));
  }, []);

  function setProduct(area: string, raw: string) {
    if (!rows) return;
    const saasProduct = raw === ungated ? null : raw;
    setRows(rows.map((r) => (r.area === area ? { ...r, saasProduct } : r)));
    setDirty(true);
    setSaved(false);
  }

  function addArea() {
    const area = newArea.trim();
    if (!area || !rows || rows.some((r) => r.area === area)) return;
    setRows([...rows, { area, saasProduct: null }]);
    setNewArea('');
    setDirty(true);
    setSaved(false);
  }

  function removeArea(area: string) {
    if (!rows) return;
    setRows(rows.filter((r) => r.area !== area));
    setDirty(true);
    setSaved(false);
  }

  async function save() {
    if (!rows) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await agencyApi.saveGating(rows);
      if (updated.length || rows.length === 0) setRows(updated);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <SectionHeader
        title="Content gating"
        subtitle="Map a content area to the SaaS product that unlocks it. Unmapped areas stay ungated (visible to everyone)."
        action={
          <div className="flex items-center gap-3">
            {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
            <Button onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save gating'}
            </Button>
          </div>
        }
      />

      {error && <ErrorNote error={error} />}
      {!rows && !error && <Spinner />}

      {rows && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Content area</th>
                <th className="px-4 py-3 font-medium">Requires SaaS product</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-slate-400">
                    No gating rules — all content is ungated.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.area}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.area}</td>
                  <td className="px-4 py-3">
                    <Select value={r.saasProduct ?? ungated} onChange={(v) => setProduct(r.area, v)}>
                      <option value={ungated}>Ungated (everyone)</option>
                      {saasProducts.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                      {/* keep a custom current value selectable even if not in the plan list */}
                      {r.saasProduct && !saasProducts.includes(r.saasProduct) && (
                        <option value={r.saasProduct}>{r.saasProduct}</option>
                      )}
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => removeArea(r.area)}
                      className="text-xs text-slate-400 hover:text-rose-600"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
            <input
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addArea()}
              placeholder="Add a content area (e.g. payments)…"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
            <Button variant="secondary" onClick={addArea} disabled={!newArea.trim()}>
              Add area
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
